import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import {
  APP_VERSION_LABELS,
  AppKey,
  getUpdateNotificationHistory,
  UpdateNotificationRecord,
} from '../../services/MasterAdmin/appConfigService';

const AppUpdateNotificationHistoryPage: React.FC = () => {
  const [list, setList] = useState<UpdateNotificationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterAppKey, setFilterAppKey] = useState<'' | AppKey>('');

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getUpdateNotificationHistory(
        50,
        filterAppKey || undefined
      );
      if (res.success && res.data) {
        setList(res.data);
      } else {
        setList([]);
      }
    } catch (err: any) {
      console.error('Error fetching update notification history:', err);
      setError(err?.response?.data?.error || 'Failed to load notification history.');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [filterAppKey]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return (
    <div className="w-full min-w-0 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link
            to="/app-version-control"
            className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to App Version Control
          </Link>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl">
            Track sent &quot;Update available&quot; notifications. Each row is one send (version + count).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterAppKey}
            onChange={(e) => setFilterAppKey(e.target.value as '' | AppKey)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200"
          >
            <option value="">All apps</option>
            <option value="user">{APP_VERSION_LABELS.user}</option>
            <option value="plus">{APP_VERSION_LABELS.plus}</option>
          </select>
          <button
            type="button"
            onClick={fetchHistory}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          {list.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No update notifications sent yet. Use &quot;Send update notification&quot; on App Version Control to notify all users.
            </div>
          ) : (
            <div className="ma-table-scroll scrollbar-light">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      App
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Android
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      iOS
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Sent at
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Target (devices)
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Sent
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Failed
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Skipped (updated)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Message
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {list.map((row) => (
                    <tr key={row.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {row.appKey
                          ? APP_VERSION_LABELS[row.appKey] ?? row.appKey
                          : APP_VERSION_LABELS.user}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {(row.androidVersion ?? row.version) || '—'}
                        {row.androidVersionCode != null || row.versionCode != null
                          ? ` (${row.androidVersionCode ?? row.versionCode})`
                          : ''}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {row.iosVersion
                          ? `${row.iosVersion}${row.iosVersionCode != null ? ` (${row.iosVersionCode})` : ''}`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {row.sentAt
                          ? new Date(row.sentAt).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">
                        {row.totalTarget}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400">
                        {row.successCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400">
                        {row.failureCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                        {row.skippedUpToDate ?? 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                        {row.updateMessage || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="text-sm text-gray-500 dark:text-gray-400">
        Total records shown: {list.length}. Data is stored in Firebase Firestore (app_update_notifications).
      </div>
    </div>
  );
};

export default AppUpdateNotificationHistoryPage;
